/**
 * This module is a wrapper of the sessionStorage and localStorage APIs but with two improvements
 * that those APIs don't have:
 * 1. The APIs only allow to save strings. If you want to save any other type, you will need to
 * stringify the data as a json when saving it and parse it when reading it. This module handles
 * that automatically, you can save any type of data: numbers, booleans, object, etc.
 * 2. You can pass a expiry date to the data you save, so it behaves like a cookie.
 * For example, you can save an item with an expiry date of a day. If you try to access the item
 * from the storage after that time, it will return null and the item will be deleted if you try
 * to read it. If you don't pass an expiry date, the data won't be deleted.
 */
(function webStorageHandler(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};

  const previousModule = global.NewHomeSource.WebStorageHandler;

  /**
   * @constructs NewHomeSource.WebStorageHandler
   */
  function WebStorageHandler() { }

  const CONFIGURATION = (function () {
    const constants = {
      localStorage: 'localStorage',
      sessionStorage: 'sessionStorage',
    };

    const get = function (key) {
      return constants[key];
    };

    return {
      get
    };
  }());

  /**
   * @private
   * @description Helper to parse a string to json, handles the exception if the string is not parseable.
   * @param {string} string The string to check if can be parsed to Json.
   * @returns {any} Json object if the string is parseable, null otherwise.
   */
  function parseToJson(string) {
    let json;

    try {
      json = JSON.parse(string);
    } catch (e) {
      json = null;
    }

    return json;
  }

  /**
   * @private
   * @param {string} key The name of the key.
   * @param {string} value The value to save for that key.
   * @param {number} maxAgeInSeconds The amount of time in seconds the item should be valid.
   * @param {string} storageType The storage api we want to use.
   */
  function setItemInStorage(key, value, maxAgeInSeconds, storageType) {
    if (!global.localStorage) {
      return;
    }

    const data = {
      creationDate: new Date().getTime(),
      maxAgeInSeconds: maxAgeInSeconds > 0
        ? maxAgeInSeconds
        : 0
    };

    // workaround for resource combiner because it doesn't allows me to do something like [key]: value
    // inside a json object declaration. just die already please.
    // ToDo remove this workaround when migrating this module to gulp.
    data[key] = value;

    if (storageType === CONFIGURATION.get('localStorage')) {
      global.localStorage.setItem(key, JSON.stringify(data));
    } else if (storageType === CONFIGURATION.get('sessionStorage')) {
      global.sessionStorage.setItem(key, JSON.stringify(data));
    }
  }

  /**
   * @private
   * @description Removes a value stored in the browser's api storage.
   * @param {any} key The key to remove from storage.
   * @param {any} storageType The storage api we want to use.
   */
  function removeItemFromStorage(key, storageType) {
    if (!global[storageType]) {
      return;
    }

    global[storageType].removeItem(key);
  }

  /**
   * @description Gets a value stored in the browser's api storage.
   * @param {string} key The name of the key.
   * @param {string} storageType The storage api we want to use.
   * @returns The item stored in the browser's storage.
   */
  function getItemFromStorage(key, storageType) {
    if (!global[storageType]) {
      return null;
    }

    let data = global[storageType].getItem(key);

    if (data === null) {
      return null;
    }

    data = parseToJson(data);

    if (data === null || data[key] === undefined) {
      return null;
    }

    if (data.maxAgeInSeconds === 0) {
      return data[key];
    }

    const currentDate = new Date().getTime();
    const differenceInSeconds = Math.floor((currentDate - data.creationDate) / 1000);

    if (differenceInSeconds > data.maxAgeInSeconds) {
      global[storageType].removeItem(key);
      return null;
    }

    return data[key];
  }

  /**
   * @public
   * @description: Saves a key/value pair in the browser's local storage,
   * if the browser supports the Window.localStorage api.
   * @param {string} key The name of the key.
   * @param {any} value The value to save for that key.
   * @param {number} maxAgeInSeconds The amount of time in seconds the item should be valid.
   * If not provided, the item will always be valid.
   */
  WebStorageHandler.setLocalStorageItem = function (key, value, maxAgeInSeconds) {
    const storageType = CONFIGURATION.get('localStorage');
    setItemInStorage(key, value, maxAgeInSeconds, storageType);
  };

  /**
  * @public
  * @description: Saves a key/value pair in the browser's session storage,
  * if the browser supports the Window.sessionStorage api.
  * @param {string} key The name of the key.
  * @param {any} value The value to save for that key.
  * @param {number} maxAgeInSeconds The amount of time the item should be valid, in seconds.
  * If not provided, the item will always be valid.
  */
  WebStorageHandler.setSessionStorageItem = function (key, value, maxAgeInSeconds) {
    const storageType = CONFIGURATION.get('sessionStorage');
    setItemInStorage(key, value, maxAgeInSeconds, storageType);
  };

  /**
   * @public
   * @description Removes a key/value stored in the browser's session storage,
   * if the browser supports the Window.sessionStorage api.
   * This method should only be used to remove data saved in the session storage
   * with the WebStorageHandler.setSessionStorageItem method.
   * @param {any} key The key to remove from session storage.
   */
  WebStorageHandler.removeSessionStorageItem = function (key) {
    const storageType = CONFIGURATION.get('sessionStorage');
    removeItemFromStorage(key, storageType);
  };

  /**
  * @public
  * @description Removes a key/value stored in the browser's local storage,
  * if the browser supports the Window.localStorage api.
  * This method should only be used to remove data saved in the local storage
  * with the WebStorageHandler.setLocalStorageItem method.
  * @param {any} key The key to remove from session storage.
  */
  WebStorageHandler.removeLocalStorageItem = function (key) {
    const storageType = CONFIGURATION.get('localStorage');
    removeItemFromStorage(key, storageType);
  };

  /**
   * @public
   * @description Gets a value stored in the browser's local storage,
   * if the browser supports the Window.localStorage api (returns null otherwise).
   * This method should only be used to retrieve data saved in the local storage
   * with the WebStorageHandler.setLocalStorageItem method.
   * @param {string} key The name of the key to retrieve from the local storage
   * @returns The item stored in the local storage.
   */
  WebStorageHandler.getLocalStorageItem = function (key) {
    const storageType = CONFIGURATION.get('localStorage');

    return getItemFromStorage(key, storageType);
  };

  /**
  * @public
  * @description Gets a value stored in the browser's session storage,
  * if the browser supports the Window.sessionStorage api (returns null otherwise).
  * This method should only be used to retrieve data saved in the session storage
  * with the WebStorageHandler.setSessionStorageItem method.
  * @param {string} key The name of the key to retrieve from the session storage
  * @returns The item stored in the local storage.
  */
  WebStorageHandler.getSessionStorageItem = function (key) {
    const storageType = CONFIGURATION.get('sessionStorage');

    return getItemFromStorage(key, storageType);
  };

  /**
   * @public
   * noConflict: Returns control of NewHomeSource.SampleModule to the previous script.
   */
  WebStorageHandler.noConflict = function noConflict() {
    global.NewHomeSource.WebStorageHandler = previousModule;
    return WebStorageHandler;
  };

  global.NewHomeSource.WebStorageHandler = WebStorageHandler;

  return WebStorageHandler;
}(window));
